import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { LoadStatus } from '../../constants/constants';
import api from '../../services/DataLayer';
import { controlSorter } from '../../utils/sortHelpers';

export const Criticality = Object.freeze({
  INFO: 'info',
  MINOR: 'minor',
  MAJOR: 'major',
  list() {
    return [this.MAJOR, this.MINOR, this.INFO];
  },
});

export const FindingFilters = Object.freeze({
  FRAMEWORK: 'fw',
  OWNER: 'owner',
  CRITICALITY: 'criticality',
});

export const FindingStatus = Object.freeze({
  PENDING: 'pending',
  RESOLVED: 'resolved',
});

export const UsualFindings = Object.freeze({
  CONTRACT_CLAUSES: 'contractClauses',
  OPERATION_CONTROL: 'operationControl',
  LACK_MANAGEMENT: 'lackManagement',
  AWARENESS_COMMUNICATION: 'awarenessCommunication',
  MANAGEMENT_OVERSIGHT: 'managementOversight',
  CONTROL_IMPLEMENTATION: 'controlImplementation',
  list() {
    return Object.values(this).slice(0, -1);
  },
});

export const QuickTags = Object.freeze({
  CORRECTIVE_COMPELETE: 'correctiveComplete',
  QT_1: 'qt1',
  QT_2: 'qt2',
  QT_3: 'qt3',
  QT_4: 'qt4',
  QT_5: 'qt5',
  list() {
    return Object.values(this).slice(0, -1);
  },
});

const initialState = {
  currentReferenceId: '',
  findings: [],
  findingLoadStatus: LoadStatus.INITIAL,
  selectedFinding: {},
};

export const initializeByAssessment = createAsyncThunk(
  'findings/initializeByAssessment',
  async (assessmentId, { getState }) => {
    if (getState().findings.currentReferenceId !== assessmentId) {
      let findings = [];
      try {
        findings = await api.getAssessmentFindings(assessmentId);
      } catch (e) {
        // TODO: 404
      }
      return { findings, id: assessmentId };
    }
    return undefined;
  },
);

export const initializeByOrganization = createAsyncThunk(
  'findings/initializeByOrganization',
  async (organizationId, { getState }) => {
    if (getState().findings.currentReferenceId !== organizationId) {
      let findings = [];
      try {
        findings = await api.getOrganizationFindings(organizationId);
      } catch (e) {
        // TODO: 404
      }
      return { findings, id: organizationId };
    }
    return undefined;
  },
);

export const saveFinding = createAsyncThunk(
  'findings/saveFinding',
  async (findingFormData, { getState, rejectWithValue }) => {
    const findingData = {
      ...findingFormData,
      parentId: findingFormData.parentId ?? getState().organizations.selectedOrganization.id,
      assessmentId: findingFormData.assessmentId ?? getState().assessments.selectedAssessment.id,
      frameworkId: findingFormData.frameworkId ?? getState().assessments.selectedAssessment.frameworkId,
    };
    try {
      if (findingData.id) {
        const _ = await api.updateFinding(findingData.assessmentId, findingData);
      } else {
        const findingId = await api.createFinding(findingData.assessmentId, findingData);
        findingData.id = findingId;
      }
      return findingData;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const deleteFinding = createAsyncThunk(
  'findings/deleteFinding',
  async (findingId, { getState, rejectWithValue }) => {
    try {
      await api.deleteFinding(getState().assessments.selectedAssessment.id, findingId);
      return findingId;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error);
    }
  },
);

export const findingSlice = createSlice({
  name: 'findings',
  initialState,
  reducers: {
    setSelectedFinding: (state, action) => {
      state.selectedFinding = state.findings.find((item) => item.id === action.payload);
    },
    clearSelectedFinding: (state, _) => {
      state.selectedFinding = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initializeByAssessment.pending, (state) => {
        state.findingLoadStatus = LoadStatus.IN_PROGRESS;
      })
      .addCase(initializeByAssessment.fulfilled, (state, action) => {
        if (action.payload !== undefined) {
          state.currentReferenceId = action.payload.id;
          state.findings = action.payload.findings.sort(controlSorter);
        }
        state.findingLoadStatus = LoadStatus.SUCCESS;
      })
      .addCase(initializeByOrganization.pending, (state) => {
        state.findingLoadStatus = LoadStatus.IN_PROGRESS;
      })
      .addCase(initializeByOrganization.fulfilled, (state, action) => {
        if (action.payload !== undefined) {
          state.currentReferenceId = action.payload.id;
          state.findings = action.payload.findings.sort(controlSorter);
        }
        state.findingLoadStatus = LoadStatus.SUCCESS;
      })
      .addCase(saveFinding.fulfilled, (state, action) => {
        const index = state.findings.findIndex((finding) => finding.id === action.payload.id);
        const newState = [...state.findings];
        if (index >= 0) {
          newState[index] = action.payload;
        } else {
          newState.unshift(action.payload);
        }
        state.findings = newState.sort(controlSorter);
        state.findingLoadStatus = LoadStatus.SUCCESS;
      })
      .addCase(saveFinding.rejected, (state, action) => {
        state.findingLoadStatus = LoadStatus.FAILURE;
      })
      .addCase(deleteFinding.fulfilled, (state, action) => {
        const newState = [...state.findings.filter((x) => x.id !== action.payload)];
        state.findings = newState;
      })
      .addCase(deleteFinding.rejected, (_, __) => {
        // TODO: error handling
      })
      .addCase('resetState', (_, __) => ({ ...initialState }));
  },
});

const { actions, reducer } = findingSlice;

export const { setSelectedFinding, clearSelectedFinding } = actions;
export default reducer;

export const selectSelectedFinding = (state) => state.findings.selectedFinding;
export const selectFindingsList = (state) => state.findings.findings;
export const selectFindingLoadStatus = (state) => state.findings.findingLoadStatus;

export const selectFindingTitle = createSelector(
  [selectFindingsList, (_, findingId) => findingId],
  (findingList, findingId) => findingList.find((finding) => finding.id === findingId)?.title || '',
);

export const selectControlTitles = (state) => state.catalogs.controlTitles;
export const selectFindingsWithTitles = createSelector(
  [selectFindingsList, selectControlTitles],
  (findings, titles) => {
    const findingsWithTitles = findings.map((finding) => ({
      ...finding,
      controlTitle: titles[finding.controlId],
    }));
    return findingsWithTitles;
  },
);

export const selectControlFindings = createSelector(
  [selectFindingsList, (_, controlId) => controlId],
  (findingList, controlId) => findingList.filter((finding) => finding.controlId === controlId),
);
